import {
  AppBar,
  Box,
  Collapse,
  Drawer,
  Grid,
  Hidden,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ExpandLess, ExpandMore, Menu } from '@material-ui/icons';
import { Fragment, FunctionComponent, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import connectMindsetLogo from '../../assets/svgs/logos/connect-mindset-logo.svg';
import { useLinkedPatients } from '../../hooks/useLinkedPatients';
import { appLayoutRoutes } from '../../routes/RoutesSelection';
import { ChildElementType } from '../../routes/types';
import { colors } from '../../theme/colors';

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: colors.xLightGray,
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  drawer: {
    width: 300,
    [theme.breakpoints.down('md')]: {
      width: 0,
    },
  },
  toolbar: theme.mixins.toolbar,
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },
  drawerPaper: {
    width: 300,
    background: colors.xLightGray,
    borderTopRightRadius: 30,
    borderBottomRightRadius: 30,
    [theme.breakpoints.down('md')]: {
      width: 200,
    },
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  menuItem: {
    color: 'inherit',
    textDecoration: 'inherit',
    '&:hover': {
      textDecoration: 'inherit',
    },
  },
  menu: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  menuItemActive: {
    '& > div': {
      backgroundColor: colors.gray,
      '&:hover': {
        backgroundColor: colors.gray,
      },
    },
    '& .MuiTypography-root': {
      fontWeight: 700,
    },
  },
  brand: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 45,
    [theme.breakpoints.down('md')]: { paddingLeft: 5 },
  },
  listItem: {
    paddingLeft: 30,
    paddingRight: 12,
    [theme.breakpoints.down('md')]: { minWidth: 30, paddingLeft: 0 },
  },
  nested: {
    paddingLeft: 60,
    [theme.breakpoints.down('md')]: { minWidth: 30, paddingLeft: 20 },
  },
}));

export const AppLayout: FunctionComponent = ({ children }) => {
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = useState(false);
  const linkedPatients = useLinkedPatients();
  const hasLinkedPatients = linkedPatients && linkedPatients.length > 0;

  // Creates a memoized version of the routes with dynamic visibility logic
  const routes = useMemo(() => {
    return appLayoutRoutes.map((route) => {
      if (route.id === 'linked-patients') {
        return {
          ...route,
          showInMenu: hasLinkedPatients,
        };
      }
      return route;
    });
  }, [hasLinkedPatients]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const [menuOpen, setMenuOpen] = useState(true);

  const handleNestedMenuClick = () => {
    setMenuOpen(!menuOpen);
  };

  const drawer = (
    <div>
      <Grid xs={12} container item alignItems="center" className={classes.brand}>
        <img src={connectMindsetLogo} alt="mindset health logo" width={200} />
      </Grid>

      <List className={classes.menu}>
        {routes
          .filter((oneRoute) => oneRoute.showInMenu)
          .map(({ id, name, path, icon, children, externalLink }) => {
            const visibleChildren: ChildElementType[] = Array.isArray(children)
              ? children.filter((child) => child.showInMenu)
              : [];
            return (
              <Fragment key={id}>
                {externalLink === true ? (
                  <Link href={path} target="_blank" rel="noreferrer" className={classes.menuItem}>
                    <ListItem button>
                      <ListItemIcon className={classes.listItem}>
                        <img src={icon} alt="menu item icon" />
                      </ListItemIcon>
                      <ListItemText primary={name} />
                    </ListItem>
                  </Link>
                ) : (
                  <NavLink
                    to={path}
                    exact
                    activeClassName={classes.menuItemActive}
                    className={classes.menuItem}
                  >
                    <ListItem button onClick={handleNestedMenuClick}>
                      <ListItemIcon className={classes.listItem}>
                        <img src={icon} alt="menu item icon" />
                      </ListItemIcon>
                      <ListItemText primary={name} />
                      {<ToggleMenu open={menuOpen} nestedItems={visibleChildren.length > 0} />}
                    </ListItem>
                  </NavLink>
                )}
                {visibleChildren.length > 0 && (
                  <Collapse in={menuOpen} timeout="auto" unmountOnExit>
                    {visibleChildren.map((nestedItem) => {
                      return (
                        <NavLink
                          to={nestedItem.path}
                          exact
                          key={nestedItem.id}
                          activeClassName={classes.menuItemActive}
                          className={classes.menuItem}
                        >
                          <List component="div" disablePadding>
                            <ListItem button>
                              <ListItemIcon className={`${classes.listItem} ${classes.nested}`}>
                                <img
                                  src={nestedItem.icon}
                                  alt="menu item icon"
                                  style={{ maxWidth: 24 }}
                                />
                              </ListItemIcon>
                              <ListItemText primary={nestedItem.name} key={nestedItem.id} />
                            </ListItem>
                          </List>
                        </NavLink>
                      );
                    })}
                  </Collapse>
                )}
              </Fragment>
            );
          })}
      </List>
    </div>
  );

  return (
    <Box display="flex">
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            className={classes.menuButton}
          >
            <Menu />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        flexGrow={1}
        display="flex"
        flexDirection="row"
        minHeight="100vh"
        data-testid="app-layout"
      >
        <Box className={classes.drawer}>
          <nav className={classes.drawer} aria-label="navigation">
            <Hidden lgUp implementation="js">
              <Drawer
                variant="temporary"
                anchor="left"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                classes={{ paper: classes.drawerPaper }}
                ModalProps={{
                  keepMounted: true /*Better open performance on mobile. */,
                }}
              >
                {drawer}
              </Drawer>
            </Hidden>
            <Hidden mdDown implementation="js">
              <Drawer classes={{ paper: classes.drawerPaper }} variant="permanent" open>
                {drawer}
              </Drawer>
            </Hidden>
          </nav>
        </Box>

        <Box flexGrow={1}>
          <Grid container item xs={12} spacing={1}>
            <main className={classes.content}>
              <div className={classes.toolbar} />
              {children}
            </main>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

type ToggleMenuProps = {
  nestedItems: boolean;
  open: boolean;
};

const ToggleMenu: FunctionComponent<ToggleMenuProps> = (props) => {
  if (props.nestedItems) {
    return props.open ? <ExpandLess /> : <ExpandMore />;
  } else {
    return <></>;
  }
};
